import { Controller } from "@hotwired/stimulus"

// Right-click context menu on the DM Notes panel to reveal or unreveal dm_notes
// for specific campaigns. campaigns value: [{id, name, revealed, reveal_id}]
export default class extends Controller {
  static values = {
    articleId: Number,
    campaigns: Array
  }

  connect() {
    this._menu = null
    this._onContextmenu = this._handleContextmenu.bind(this)
    this._onDocClick = this._dismiss.bind(this)
    this.element.addEventListener("contextmenu", this._onContextmenu)
  }

  disconnect() {
    this.element.removeEventListener("contextmenu", this._onContextmenu)
    this._dismiss()
  }

  _handleContextmenu(event) {
    if (this.campaignsValue.length === 0) return
    event.preventDefault()
    this._dismiss()
    this._showMenu(event.clientX, event.clientY)
    // Defer so this click doesn't immediately dismiss via document listener
    setTimeout(() => document.addEventListener("click", this._onDocClick, { once: true }), 0)
  }

  _showMenu(x, y) {
    const menu = document.createElement("div")
    menu.className = [
      "fixed z-50 min-w-48 py-1 rounded-lg shadow-lg",
      "bg-tavern-surface border border-tavern-border",
      "text-sm text-tavern-cream"
    ].join(" ")

    const header = document.createElement("div")
    header.className = "px-3 py-1.5 text-xs text-tavern-muted font-heading tracking-wide border-b border-tavern-border mb-1"
    header.textContent = "Reveal DM notes to campaign"
    menu.appendChild(header)

    this.campaignsValue.forEach(c => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = [
        "w-full text-left px-3 py-2 flex items-center gap-2",
        "hover:bg-tavern-raised transition-colors"
      ].join(" ")

      const icon = document.createElement("span")
      icon.className = c.revealed ? "text-tavern-gold" : "text-tavern-muted"
      icon.innerHTML = c.revealed
        ? `<svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"/></svg>`
        : `<svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>`

      const label = document.createElement("span")
      label.textContent = c.revealed ? `Unreveal from ${c.name}` : `Reveal to ${c.name}`

      item.appendChild(icon)
      item.appendChild(label)
      item.addEventListener("click", (e) => {
        e.stopPropagation()
        this._toggle(c)
        this._dismiss()
      })
      menu.appendChild(item)
    })

    // Position: keep inside viewport
    menu.style.left = `${Math.min(x, window.innerWidth - 220)}px`
    menu.style.top = `${Math.min(y, window.innerHeight - 40 - this.campaignsValue.length * 38)}px`

    document.body.appendChild(menu)
    this._menu = menu
  }

  _toggle(campaign) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const headers = { "Content-Type": "application/json", "X-CSRF-Token": csrfToken }
    const base = `/articles/${this.articleIdValue}/article_reveals`

    if (campaign.revealed) {
      fetch(`${base}/${campaign.reveal_id}`, { method: "DELETE", headers })
        .then(r => r.json())
        .then(data => {
          if (data.revealed === false) {
            this.campaignsValue = this.campaignsValue.map(c =>
              c.id === campaign.id ? { ...c, revealed: false, reveal_id: null } : c
            )
          }
        })
    } else {
      fetch(base, {
        method: "POST",
        headers,
        body: JSON.stringify({ campaign_id: campaign.id })
      })
        .then(r => r.json())
        .then(data => {
          if (data.reveal_id) {
            this.campaignsValue = this.campaignsValue.map(c =>
              c.id === campaign.id ? { ...c, revealed: true, reveal_id: data.reveal_id } : c
            )
          }
        })
    }
  }

  _dismiss() {
    if (this._menu) {
      this._menu.remove()
      this._menu = null
    }
  }
}
